import {
  TimeframeConditionType,
  TimeframeUnit,
} from "@redotech/redo-customer-segments-common/marketing/segments/segment-timeframe";
import {
  ActivityCountType,
  CustomerCharacteristicType,
  CustomerDataSource,
  SegmentConditionType,
} from "@redotech/redo-customer-segments-common/marketing/segments/segment-types";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import { Integration } from "@redotech/redo-flows-common/integrations";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex, FlexProps } from "@redotech/redo-web/flex";
import { interleave, remove, replace } from "@redotech/util/array";
import { assertNever } from "@redotech/util/type";
import { createContext, memo, useContext, useMemo } from "react";
import { SegmentActivityCondition } from "./activities/segment-activity-condition";
import { SegmentCharacteristicCondition } from "./characteristics/segment-characteristic-condition";
import { ConditionConnector } from "./condition-connector";
import { SegmentCustomEventCondition } from "./custom-events/segment-custom-event-condition";
import { SegmentConditionTypeSelect } from "./segment-condition-type-select";
import { SegmentConjunctionSelect } from "./segment-conjunction-select";
import {
  SegmentConditionBlockInProgress,
  SegmentConditionInProgress as SegmentConditionInProgressType,
  SegmentInProgress,
  isSegmentConditionUninitialized,
} from "./segment-in-progress";

export enum SegmentEditorLocation {
  SEGMENT_BUILDER = "segment_builder",
  AUTOMATIONS_PANEL = "automations_panel",
}

export const SegmentBuilderLocationContext =
  createContext<SegmentEditorLocation>(SegmentEditorLocation.SEGMENT_BUILDER);

export const SegmentBuilderEditor = memo(function SegmentBuilderEditor({
  segment,
  setSegment,
  availableDataSourcesForOrders,
  availableDataSourcesForMarketing,
  enabledIntegrations,
  hiddenCharacteristics,
}: {
  segment: SegmentInProgress;
  setSegment: (segment: SegmentInProgress) => void;
  availableDataSourcesForOrders?: CustomerDataSource[];
  availableDataSourcesForMarketing?: CustomerDataSource[];
  enabledIntegrations?: Record<Integration, boolean>;
  hiddenCharacteristics?: Set<CustomerCharacteristicType>;
}) {
  const finalConditionBlock: SegmentConditionBlockInProgress | undefined =
    segment.conditionBlocks[segment.conditionBlocks.length - 1];
  const finalConditionIsUninitialized =
    finalConditionBlock?.conditions.length === 0 ||
    (finalConditionBlock?.conditions.length === 1 &&
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      isSegmentConditionUninitialized(finalConditionBlock.conditions[0]));

  const conditionElements = segment.conditionBlocks.map((block, index) => (
    <SegmentConditionBlock
      availableDataSourcesForMarketing={availableDataSourcesForMarketing}
      availableDataSourcesForOrders={availableDataSourcesForOrders}
      block={block}
      deleteBlockRequested={() => {
        setSegment({
          ...segment,
          conditionBlocks: remove(segment.conditionBlocks, index),
        });
      }}
      enabledIntegrations={enabledIntegrations}
      hiddenCharacteristics={hiddenCharacteristics}
      key={index}
      setBlock={(block) => {
        setSegment({
          ...segment,
          conditionBlocks: replace(segment.conditionBlocks, index, block),
        });
      }}
    />
  ));

  const joinedConditions = interleave(conditionElements, (_, idx) => (
    <ConditionConnector conjunction={segment.mode} key={`connector-${idx}`} />
  ));

  return (
    <Flex align="flex-start" grow={1} justify="center" p="5xl">
      <Flex
        dir="column"
        gap="xl"
        style={{ maxWidth: "80%", minWidth: "500px" }}
      >
        <SegmentConjunctionSelect
          mode={segment.mode}
          setMode={(mode) => {
            setSegment({ ...segment, mode });
          }}
        />

        <Flex dir="column" gap="none">
          {joinedConditions}
        </Flex>
        {!finalConditionIsUninitialized && (
          <Flex>
            <RedoButton
              hierarchy="secondary"
              onClick={() => {
                setSegment({
                  ...segment,
                  conditionBlocks: [
                    ...segment.conditionBlocks,
                    emptySegmentConditionBlock,
                  ],
                });
              }}
              text="+ Add condition group"
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

const emptySegmentConditionBlock: SegmentConditionBlockInProgress = {
  mode: ConjunctionMode.OR,
  conditions: [{ type: "empty" }],
};

export const blockBorderProps: FlexProps = {
  bgColor: "primary",
  borderColor: "primary",
  borderStyle: "solid",
  borderWidth: "1px",
  borderRadius: "lg",
};

export const blockBorderErrorProps: FlexProps = {
  bgColor: "primary",
  borderColor: "error",
  borderStyle: "solid",
  borderWidth: "1px",
  borderRadius: "lg",
};

export const SegmentConditionBlock = memo(function SegmentConditionBlock({
  block,
  setBlock,
  deleteBlockRequested,
  state = "default",
  availableDataSourcesForOrders,
  availableDataSourcesForMarketing,
  enabledIntegrations,
  hiddenCharacteristics,
}: {
  block: SegmentConditionBlockInProgress;
  setBlock: (block: SegmentConditionBlockInProgress) => void;
  deleteBlockRequested?(): void;
  state?: "default" | "error";
  availableDataSourcesForOrders?: CustomerDataSource[];
  availableDataSourcesForMarketing?: CustomerDataSource[];
  enabledIntegrations?: Record<Integration, boolean>;
  hiddenCharacteristics?: Set<CustomerCharacteristicType>;
}) {
  const location = useContext(SegmentBuilderLocationContext);

  const finalCondition: SegmentConditionInProgressType | undefined =
    block.conditions[block.conditions.length - 1];
  const finalConditionIsUninitialized =
    finalCondition && isSegmentConditionUninitialized(finalCondition);

  const conditionElements = block.conditions.map((condition, index) => {
    return (
      <SegmentConditionInProgress
        availableDataSourcesForMarketing={availableDataSourcesForMarketing}
        availableDataSourcesForOrders={availableDataSourcesForOrders}
        condition={condition}
        deleteRequested={() => {
          setBlock({ ...block, conditions: remove(block.conditions, index) });
        }}
        enabledIntegrations={enabledIntegrations}
        hiddenCharacteristics={hiddenCharacteristics}
        key={index}
        setCondition={(condition) => {
          setBlock({
            ...block,
            conditions: replace(block.conditions, index, condition),
          });
        }}
      />
    );
  });

  const joinedConditions = interleave(conditionElements, (_, idx) => (
    <ConditionConnector conjunction={block.mode} key={`connector-${idx}`} />
  ));

  const padding = useMemo(() => {
    switch (location) {
      case SegmentEditorLocation.SEGMENT_BUILDER:
        return "3xl";
      case SegmentEditorLocation.AUTOMATIONS_PANEL:
        return "xl";
      default:
        assertNever(location);
    }
  }, [location]);

  const borderProps =
    state === "error" ? blockBorderErrorProps : blockBorderProps;

  return (
    <Flex {...borderProps} dir="column" gap="xl" p={padding}>
      <Flex justify="space-between">
        <SegmentConjunctionSelect
          mode={block.mode}
          setMode={(mode) => {
            setBlock({ ...block, mode });
          }}
        />
        {deleteBlockRequested && (
          <RedoButton
            aria-label="Delete"
            IconLeading={TrashIcon}
            onClick={deleteBlockRequested}
            theme="destructive"
          />
        )}
      </Flex>
      <Flex dir="column" gap="none">
        {joinedConditions}
      </Flex>
      {!finalConditionIsUninitialized && (
        <Flex>
          <RedoButton
            hierarchy="secondary"
            onClick={() => {
              setBlock({
                ...block,
                conditions: [...block.conditions, emptySegmentCondition],
              });
            }}
            text="+ Add condition"
          />
        </Flex>
      )}
    </Flex>
  );
});

const emptySegmentCondition: SegmentConditionInProgressType = { type: "empty" };

const SegmentConditionInProgress = memo(function SegmentConditionInProgress({
  condition,
  setCondition,
  deleteRequested,
  availableDataSourcesForOrders,
  availableDataSourcesForMarketing,
  enabledIntegrations,
  hiddenCharacteristics,
}: {
  condition: SegmentConditionInProgressType;
  setCondition: (condition: SegmentConditionInProgressType) => void;
  deleteRequested: () => void;
  availableDataSourcesForOrders?: CustomerDataSource[];
  availableDataSourcesForMarketing?: CustomerDataSource[];
  enabledIntegrations?: Record<Integration, boolean>;
  hiddenCharacteristics?: Set<CustomerCharacteristicType>;
}) {
  switch (condition.type) {
    case "empty":
      return (
        <SegmentConditionTypeSelect
          conditionTypeSelected={(conditionType) => {
            setCondition(baseSegmentConditionInProgress[conditionType]);
          }}
          deleteRequested={deleteRequested}
        />
      );
    case SegmentConditionType.CUSTOMER_ATTRIBUTE:
      return (
        <SegmentCharacteristicCondition
          condition={condition}
          deleteRequested={deleteRequested}
          enabledIntegrations={enabledIntegrations}
          hiddenCharacteristics={hiddenCharacteristics}
          setCondition={setCondition}
        />
      );
    case SegmentConditionType.CUSTOMER_ACTIVITY:
      return (
        <SegmentActivityCondition
          availableDataSourcesForMarketing={availableDataSourcesForMarketing}
          availableDataSourcesForOrders={availableDataSourcesForOrders}
          condition={condition}
          deleteRequested={deleteRequested}
          onCustomEventSelected={() =>
            setCondition(
              baseSegmentConditionInProgress[SegmentConditionType.CUSTOM_EVENT],
            )
          }
          setCondition={setCondition}
        />
      );
    case SegmentConditionType.CUSTOM_EVENT:
      return (
        <SegmentCustomEventCondition
          condition={condition}
          deleteRequested={deleteRequested}
          setCondition={setCondition}
        />
      );
    default:
      assertNever(condition);
  }
});

const baseSegmentConditionInProgress: Record<
  SegmentConditionType,
  SegmentConditionInProgressType
> = {
  [SegmentConditionType.CUSTOMER_ATTRIBUTE]: {
    type: SegmentConditionType.CUSTOMER_ATTRIBUTE,
  },
  [SegmentConditionType.CUSTOMER_ACTIVITY]: {
    type: SegmentConditionType.CUSTOMER_ACTIVITY,
    activityType: undefined,
    count: { type: ActivityCountType.AT_LEAST_ONCE },
    timeframe: {
      type: TimeframeConditionType.BEFORE_NOW_RELATIVE,
      units: TimeframeUnit.DAY,
    },
    whereConditions: [],
  },
  [SegmentConditionType.CUSTOM_EVENT]: {
    type: SegmentConditionType.CUSTOM_EVENT,
    eventName: undefined,
    count: { type: ActivityCountType.AT_LEAST_ONCE },
    timeframe: {
      type: TimeframeConditionType.BEFORE_NOW_RELATIVE,
      units: TimeframeUnit.DAY,
    },
    propertyFilters: [],
  },
};
